import React, { useState, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { getNormalizedLOBName } from '@xengage/gw-portals-config-js';
import { DocumentService } from 'gw-capability-policydocument';
import {
    PolicyCommonDetails,
    PolicyCommonDocuments,
    PolicyCommonContacts,
    PolicyCoverages
} from 'gw-capability-policy-common-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import metadata from './CEPLPolicyDetails.metadata.json5';
import messages from '../PolicyDetails/WniPolicyDetails.messages';
import styles from './CEPLPolicyDetails.module.scss';

const handlePrint = () => {
    window.print();
};

function CEPLPolicyDetails(props) {
    const { history, match, 
        policyCommonDetails,
        enableCEPolicyChangeButton
    } = props;

    const [writableDocuments, updateWritableDocuments] = useState([]);
    const translator = useTranslator();
    const policyNumber = _.get(match, 'params.policyNumber');

    const redirectToEndorsement = useCallback(
        (policyDetails, requestType) => {
            const productCode = _.findKey(policyDetails.lobs, (lob) => !_.isEmpty(lob));
            history.push(`/endorsement-${getNormalizedLOBName(productCode)}`,
                { policyNumber, policyDetails, requestType });
        },
        [history, policyNumber]
    );

    const getPolicyDownloadLink = useCallback(() => {
        return DocumentService.downloadPolicyDocument(
            _.get(policyCommonDetails.currentPeriodDetails, 'idCardPublicID'),
            _.get(policyCommonDetails.currentPeriodDetails, 'idCardSessionID')
        );
    }, [policyCommonDetails]);
    
    const getPolicyStatus = () => {
        const status =  _.get(policyCommonDetails.currentPeriodDetails, 'displayStatus_Ext');
        if(status === 'In Force') {
            return '- Active';
        }
        if(status === 'Scheduled') {
            return '';
        }
        return `- ${status}`;
    };

    const overrideProps = {
        pageTitle: {
            content: translator(messages.pageTitle, {
                policyNumber: policyCommonDetails.currentPeriodDetails.policyNumber,
                policyStatus: getPolicyStatus()
            })
        },
        policyChangeNotification: {
            visible: policyCommonDetails.currentPeriodDetails.hasSubmittedChange_Ext
        },
        epxiredPeriod: {
            heading: policyCommonDetails.hasExpiredPeriod ? translator(messages.expiredTab) : '',
            visible: policyCommonDetails.hasExpiredPeriod
        },
        currentPeriod: {
            heading: translator(messages.currentTab)
        },
        renewalPeriod: {
            heading: policyCommonDetails.hasRenewedPeriod ? translator(messages.renewalTab) : '',
            disabled: !policyCommonDetails.hasRenewedPeriod
        },
        changePolicyButtonId: {
            visible: enableCEPolicyChangeButton
        },
        idCardContainer: {
            visible: false
        },
        idCard: {
            href: getPolicyDownloadLink(),
            target: '_blank'
        },
        policyDocumentsSection: {
            updateWritableDocuments: updateWritableDocuments,
            writableDocuments: writableDocuments
        },
        expiredPolicyContactsSection: {
            productCode: policyCommonDetails.expiredPeriodDetails.productCode_Ext
        },
        policyContactsSection: {
            productCode: policyCommonDetails.currentPeriodDetails.productCode_Ext
        },
        renewalPolicyContactsSection: {
            productCode: policyCommonDetails.renewedPeriodDetails.productCode_Ext
        }
    };

    const resolvers = {
        resolveComponentMap: {
            policycommondetailscomponent: PolicyCommonDetails,
            policycoveragescomponent: PolicyCoverages,
            policycommondocumentscomponent: PolicyCommonDocuments,
            policycommoncontactscomponent: PolicyCommonContacts
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onPrint: handlePrint,
            onChangePolicy: () => redirectToEndorsement(policyCommonDetails.currentPeriodDetails, 'policyChange'),
            onChangePolicyRenewal: () => redirectToEndorsement(
                policyCommonDetails.renewedPeriodDetails, 'policyRenew'
            )
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={policyCommonDetails}
            overrideProps={overrideProps}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

CEPLPolicyDetails.propTypes = {
    match: PropTypes.shape({}).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    policyCommonDetails: PropTypes.object.isRequired,
    enableCEPolicyChangeButton: PropTypes.bool
};

CEPLPolicyDetails.defaultProps = {
    policyCommonDetails: {},
    enableCEPolicyChangeButton: false
};

export default CEPLPolicyDetails;
